import { styled } from "@linaria/react"
import { type IObservableValue } from "mobx"
import { observer } from "mobx-react-lite"
import { type ChangeEvent, type RefObject, useEffect, useRef } from "react"

import { PromptTextAreaProseMirrorModel } from "@/components/PromptInput/PromptTextAreaProseMirrorModel.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useMyProfile } from "@/hooks/useProfile.ts"

import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"

import "prosemirror-codemark/dist/codemark.css"
import "prosemirror-view/style/prosemirror.css"

export const PromptTextAreaProseMirror = observer((
  {
    model,
    value,
    placeholder,
    className,
    autoFocus,
    readOnly,
    richText,
    testId,
    onCancel,
    onSubmit,
    onChange,
    ref
  }: {
    model: PromptInputModel
    value: IObservableValue<string>
    placeholder: string
    className: string
    autoFocus: boolean
    readOnly: boolean
    richText: boolean
    testId: string
    onCancel?: () => void
    onSubmit?: () => void
    onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void
    ref?: RefObject<HTMLTextAreaElement | null>
  }
) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const profile = useMyProfile()

  const viewModel = useModel(() =>
    new PromptTextAreaProseMirrorModel(
      model,
      value,
      onCancel,
      onSubmit,
      onChange,
      profile,
      richText
    ), [model, value, onCancel, onSubmit, onChange, profile, richText]
  )

  useEffect(() => {
    if (!editorRef.current || viewModel.editorView.get()) return

    viewModel.initializeEditor(
      editorRef.current,
      placeholder,
      autoFocus,
      readOnly,
      ref,
    )
  }, [viewModel, placeholder, autoFocus, readOnly, ref])

  // Update editor content when the value changes externally
  const newText = value.get()
  useEffect(() => {
    viewModel.updateContent(newText)
  }, [newText, viewModel])

  return (
    <ProseMirrorContainer
      ref={editorRef}
      className={className}
      data-testid={testId}
      data-placeholder={placeholder}
    />
  )
})

const ProseMirrorContainer = styled.div`
  overflow: auto;

  .ProseMirror {
    outline: none;

    p {
      margin: 0;
    }
  }
`